import { useQuery } from "@tanstack/react-query";
import { pathPickBasketQueryApi } from "@/features/pathpick/features/basket/api/query";
import { PathPickSmartStoreInfoResponse } from "@/features/pathpick/features/basket/types/response";
import useCompanyIdentity from "@/shared/hooks/useCompanyIdentity";
import { toCamelKeyObject } from "@/utils/case";
import { getHttpStatus, isRetryableApiError } from "@/utils/axios-client-error";

// 스마트스토어 상품 등록 기본 정보 조회 — 미설정 시 에러가 될 수 있어 비suspense로 둔다.
const useGetSmartStoreInfo = () => {
  const { companyId } = useCompanyIdentity();

  const {
    data: smartStoreInfo,
    isPending,
    isFetching,
    dataUpdatedAt,
    isError,
    error,
    errorUpdatedAt,
    refetch,
  } = useQuery({
    queryKey: ["pathpick", "smart-store-info", companyId],
    queryFn: async () => {
      try {
        const data = await pathPickBasketQueryApi.getSmartStoreInfo(
          companyId as number
        );
        return toCamelKeyObject(data) as PathPickSmartStoreInfoResponse;
      } catch (err) {
        // 아직 한 번도 저장하지 않은 계정은 404다 — "미설정"은 조회 실패가 아니므로
        // 정상값(null)으로 흡수해 "저장 안 함"과 "확인 불가"를 구분한다.
        // (useQuery는 undefined 반환을 에러로 취급하므로 반드시 null이어야 한다)
        if (getHttpStatus(err) === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: Boolean(companyId),
    retry: (failureCount, err) => isRetryableApiError(err) && failureCount < 1,
  });

  // isFetching/dataUpdatedAt은 "스마트스토어 정보 다시 확인" UI(로딩·마지막 확인 시각)에서 쓴다.
  // isError/refetch는 조회 실패를 알리고 다시 시도하게 하는 모달 UI에서 쓴다.
  return {
    smartStoreInfo,
    isPending,
    isFetching,
    dataUpdatedAt,
    isError,
    error,
    // 실패한 쿼리의 error는 캐시에 남아 컴포넌트를 다시 마운트해도 그대로 읽힌다 —
    // 에러 알림이 "지난번 실패의 잔상"과 "방금 실패"를 구분하려면 확정 시각이 필요하다.
    errorUpdatedAt,
    refetch,
  };
};

export default useGetSmartStoreInfo;
